import React, { useState, useEffect, useCallback } from 'react';
import styled, { useTheme, css } from 'styled-components';
import { ActiveGameInfo } from '../../types/liveGame';
import { getActiveGames } from '../../services/liveGameService';

// Стили для Shadow Grid темы
const shadowGridChrome = css`
  ${({ theme }) => theme.name === 'shadowGrid' && css`
    position: relative;
    border: 1px solid rgba(0, 212, 255, 0.35);
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.6), 0 0 28px rgba(0, 212, 255, 0.2);
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 1px solid rgba(0, 212, 255, 0.2);
      opacity: 0.25;
      pointer-events: none;
    }
  `}
`;

interface ThemeProps {
  $night?: boolean;
}

const Container = styled.div<ThemeProps>`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div<ThemeProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const LiveIndicator = styled.div<ThemeProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.success || '#00ff88'};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const PulsingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success || '#00ff88'};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.2);
    }
  }
`;

const RefreshTimer = styled.div<ThemeProps>`
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const GamesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
`;

const GameCard = styled.div<ThemeProps & { $isLive?: boolean }>`
  background: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return theme.colors.surface;
    if ($night) return theme.colors.surface;
    return 'rgba(5, 6, 8, 0.8)';
  }};
  border: 1px solid ${({ $night, theme, $isLive }) => {
    if ($isLive) return theme.colors.success || '#00ff88';
    if (theme.name === 'modernWarfare') return theme.colors.borderLight;
    return $night ? theme.colors.borderLight : 'rgba(0, 212, 255, 0.25)';
  }};
  border-radius: ${({ $night }) => ($night ? '16px' : '12px')};
  padding: 20px;
  transition: all 0.3s ease;
  box-shadow: ${({ $night, theme, $isLive }) => {
    if ($isLive) return `0 0 20px ${theme.colors.success || '#00ff88'}40`;
    return $night ? theme.shadows.card : '0 10px 30px rgba(0, 0, 0, 0.4)';
  }};
  ${({ theme }) => theme.name === 'shadowGrid' && shadowGridChrome};

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => `0 0 25px ${theme.colors.primary}50`};
  }
`;

const GameHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const MissionInfo = styled.div`
  flex: 1;
`;

const MissionName = styled.h4<ThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : '#00d4ff')};
  margin: 0 0 5px 0;
  font-size: 1.1rem;
  font-weight: bold;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Rajdhani', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  text-transform: ${({ $night }) => ($night ? 'uppercase' : 'none')};
  letter-spacing: ${({ $night }) => ($night ? '0.5px' : 'normal')};
`;

const GameType = styled.span<ThemeProps>`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${({ theme }) => theme.colors.primaryDark || 'rgba(0, 212, 255, 0.2)'};
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const LiveBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  background: linear-gradient(135deg, #00ff88, #00cc66);
  color: #000;
  font-size: 0.7rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const GameStats = styled.div<ThemeProps>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 15px;
`;

const StatItem = styled.div<ThemeProps>`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.span<ThemeProps>`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const StatValue = styled.span<ThemeProps>`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: bold;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const LastEvent = styled.div<ThemeProps>`
  padding: 12px;
  background: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return theme.colors.surfaceHover;
    return $night ? 'rgba(0, 212, 255, 0.1)' : 'rgba(0, 212, 255, 0.05)';
  }};
  border-radius: 8px;
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
`;

const LastEventLabel = styled.div<ThemeProps>`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const LastEventName = styled.div<ThemeProps>`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const MapInfo = styled.div<ThemeProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderMuted || 'rgba(0, 212, 255, 0.15)'};
`;

const MapIcon = styled.span`
  font-size: 1rem;
`;

const MapName = styled.span<ThemeProps>`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const EmptyState = styled.div<ThemeProps>`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
  padding: 60px 20px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 15px;
  opacity: 0.5;
`;

const LoadingSpinner = styled.div<ThemeProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;

  &::after {
    content: '';
    width: 50px;
    height: 50px;
    border: 5px solid ${({ theme }) => theme.colors.borderLight || 'rgba(0, 212, 255, 0.3)'};
    border-top: 5px solid ${({ theme }) => theme.colors.primary || '#00d4ff'};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div<ThemeProps>`
  text-align: center;
  color: ${({ theme }) => theme.colors.error || '#ff5467'};
  padding: 40px 20px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

interface LiveGamesSectionProps {
  fieldId: string;
  refreshInterval?: number; // в миллисекундах, по умолчанию 15000 (15 секунд)
  className?: string;
}

const LiveGamesSection: React.FC<LiveGamesSectionProps> = ({
  fieldId,
  refreshInterval = 15000,
  className
}) => {
  const theme = useTheme();
  const isNightOps = theme.name === 'nightOps';

  const [games, setGames] = useState<ActiveGameInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(refreshInterval / 1000);

  const loadGames = useCallback(async () => {
    try {
      setError(null);
      const activeGames = await getActiveGames(fieldId);
      setGames(activeGames);
    } catch (err: any) {
      setError(err.message || 'Failed to load live games');
    } finally {
      setLoading(false);
    }
  }, [fieldId]);

  // Первоначальная загрузка
  useEffect(() => {
    loadGames();
  }, [loadGames]);

  // Авто-обновление
  useEffect(() => {
    const refreshTimer = setInterval(() => {
      loadGames();
      setSecondsUntilRefresh(refreshInterval / 1000);
    }, refreshInterval);

    const countdownTimer = setInterval(() => {
      setSecondsUntilRefresh(prev => (prev > 0 ? prev - 1 : refreshInterval / 1000));
    }, 1000);

    return () => {
      clearInterval(refreshTimer);
      clearInterval(countdownTimer);
    };
  }, [loadGames, refreshInterval]);

  // Форматирование времени
  const formatDuration = (startedAt: string): string => {
    const start = new Date(startedAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatLastEvent = (timestamp: string): string => {
    const eventTime = new Date(timestamp);
    const now = new Date();
    const diffSec = Math.floor((now.getTime() - eventTime.getTime()) / 1000);

    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    return `${Math.floor(diffSec / 3600)}h ago`;
  };

  if (loading) {
    return (
      <Container $night={isNightOps} className={className}>
        <LoadingSpinner $night={isNightOps} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container $night={isNightOps} className={className}>
        <ErrorMessage $night={isNightOps}>{error}</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container $night={isNightOps} className={className}>
      <Header $night={isNightOps}>
        <LiveIndicator $night={isNightOps}>
          <PulsingDot />
          Live Games
        </LiveIndicator>
        <RefreshTimer $night={isNightOps}>
          Refresh in {secondsUntilRefresh}s
        </RefreshTimer>
      </Header>

      {games.length === 0 ? (
        <EmptyState $night={isNightOps}>
          <EmptyIcon>🎮</EmptyIcon>
          No active games at the moment
        </EmptyState>
      ) : (
        <GamesGrid>
          {games.map(game => (
            <GameCard key={game.gameSessionId} $night={isNightOps} $isLive={game.isLive}>
              <GameHeader>
                <MissionInfo>
                  <MissionName $night={isNightOps}>{game.operationName}</MissionName>
                  <GameType $night={isNightOps}>{game.gameType}</GameType>
                </MissionInfo>
                {game.isLive && (
                  <LiveBadge>
                    <PulsingDot />
                    Live
                  </LiveBadge>
                )}
              </GameHeader>

              <GameStats $night={isNightOps}>
                <StatItem $night={isNightOps}>
                  <StatLabel $night={isNightOps}>Duration</StatLabel>
                  <StatValue $night={isNightOps}>{formatDuration(game.startedAt)}</StatValue>
                </StatItem>
                <StatItem $night={isNightOps}>
                  <StatLabel $night={isNightOps}>Players</StatLabel>
                  <StatValue $night={isNightOps}>{game.playersCount}</StatValue>
                </StatItem>
                <StatItem $night={isNightOps}>
                  <StatLabel $night={isNightOps}>Teams</StatLabel>
                  <StatValue $night={isNightOps}>{game.teamIds.length}</StatValue>
                </StatItem>
                <StatItem $night={isNightOps}>
                  <StatLabel $night={isNightOps}>Events</StatLabel>
                  <StatValue $night={isNightOps}>{game.eventsCount}</StatValue>
                </StatItem>
              </GameStats>

              {game.lastEventName && (
                <LastEvent $night={isNightOps}>
                  <LastEventLabel $night={isNightOps}>
                    Last Event • {formatLastEvent(game.lastEventAt)}
                  </LastEventLabel>
                  <LastEventName $night={isNightOps}>{game.lastEventName}</LastEventName>
                </LastEvent>
              )}

              {game.mapName && game.mapName !== 'Unknown Map' && (
                <MapInfo $night={isNightOps}>
                  <MapIcon>🗺️</MapIcon>
                  <MapName $night={isNightOps}>{game.mapName}</MapName>
                </MapInfo>
              )}
            </GameCard>
          ))}
        </GamesGrid>
      )}
    </Container>
  );
};

export default LiveGamesSection;
